import * as React from 'react';

import {
  Col,
  ConsoleTouchDialog,
  Popover,
  PopoverDialog,
  PopoverTrigger,
  Row,
  TouchButton,
  TouchTextField,
  cn,
} from '@rematter/ui-web';
import { LucideTrash2 } from 'lucide-react';

import { useTranslations } from '../../../i18n/useTranslations';

import { useTheme, useUIPreferences } from '~/shared/hooks';

type VoidTicketButtonProps = {
  isDisabled: boolean;
  isProcessing: boolean;
  onVoid: (voidReason?: string) => void;
  className?: string;
};

type VoidReasonFormProps = {
  reason: string;
  setReason: (v: string) => void;
  isProcessing: boolean;
  touchModeEnabled: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

//
// ⚛️ ⚛️ ⚛️ VOID REASON FORM ⚛️ ⚛️ ⚛️
//
const VoidReasonForm = ({
  reason,
  setReason,
  isProcessing,
  touchModeEnabled,
  onCancel,
  onConfirm,
}: VoidReasonFormProps) => {
  const t = useTranslations();

  return (
    <Col gap3 className="@VoidReasonForm h-full w-full p-4">
      <div className="text-sm text-neutral/80">
        {t.scaleTicketPro.voidTicket.description}
      </div>
      <TouchTextField
        label={t.scaleTicketPro.voidTicket.reasonLabel}
        placeholder={t.scaleTicketPro.voidTicket.reasonPlaceholder}
        value={reason}
        onChange={setReason}
        rows={3}
        touchModeEnabled={touchModeEnabled}
      />
      <Row xEnd gap2>
        <TouchButton variant="outline" size="md" onPress={onCancel}>
          {t.common.cancel}
        </TouchButton>
        <TouchButton
          variant="solid"
          color="failure"
          size="md"
          isDisabled={isProcessing || !reason.trim().length}
          onPress={onConfirm}
        >
          {t.scaleTicketPro.voidTicket.confirmButton}
        </TouchButton>
      </Row>
    </Col>
  );
};

//
// ⚛️ ⚛️ ⚛️ VOID TICKET BUTTON ⚛️ ⚛️ ⚛️
//
export const VoidTicketButton = ({
  isDisabled,
  isProcessing,
  onVoid,
  className,
}: VoidTicketButtonProps) => {
  const t = useTranslations();
  const { touchModeEnabled } = useUIPreferences();
  const { effectiveColorScheme } = useTheme();

  const [isOpen, setIsOpen] = React.useState(false);
  const [reason, setReason] = React.useState('');

  const handleCancel = () => {
    setReason('');
    setIsOpen(false);
  };

  const handleConfirm = () => {
    setIsOpen(false);
    onVoid(reason.trim() || undefined);
    setReason('');
  };

  return (
    <div className={cn('@VoidTicketButton', className)}>
      {touchModeEnabled ? (
        <>
          <TouchButton
            variant="outline"
            color="failure"
            isDisabled={isDisabled}
            size="xl"
            className="h-full"
            onPress={() => setIsOpen(true)}
          >
            <Row gap1>
              <LucideTrash2 size={16} /> {t.scaleTicketPro.detail.actions.void}
            </Row>
          </TouchButton>
          <ConsoleTouchDialog
            touchModeEnabled={touchModeEnabled}
            effectiveColorScheme={effectiveColorScheme}
            isOpen={isOpen}
            onOpenChange={(open) => {
              setIsOpen(open);
              if (!open) setReason('');
            }}
          >
            <VoidReasonForm
              reason={reason}
              setReason={setReason}
              isProcessing={isProcessing}
              touchModeEnabled={touchModeEnabled}
              onCancel={handleCancel}
              onConfirm={handleConfirm}
            />
          </ConsoleTouchDialog>
        </>
      ) : (
        <PopoverTrigger
          isOpen={isOpen}
          onOpenChange={(open) => {
            setIsOpen(open);
            if (!open) setReason('');
          }}
        >
          <TouchButton
            variant="outline"
            color="failure"
            isDisabled={isDisabled}
            size="xl"
            className="h-full"
            onPress={() => null}
          >
            <Row gap1>
              <LucideTrash2 size={16} /> {t.scaleTicketPro.detail.actions.void}
            </Row>
          </TouchButton>
          <Popover>
            <PopoverDialog className="max-w-[320px]">
              <VoidReasonForm
                reason={reason}
                setReason={setReason}
                isProcessing={isProcessing}
                touchModeEnabled={false}
                onCancel={handleCancel}
                onConfirm={handleConfirm}
              />
            </PopoverDialog>
          </Popover>
        </PopoverTrigger>
      )}
    </div>
  );
};
